import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Bell, Check } from "lucide-react";
import api from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export function NotificationBell({ accent = "#1D9BF0" }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { data } = useQuery({
    queryKey: ["notifications"],
    queryFn: async () => (await api.get("/notifications")).data,
    refetchInterval: 20000,
  });
  const items = data?.items || [];
  const unread = data?.unread || 0;

  const readAll = async () => { await api.post("/notifications/read-all"); qc.invalidateQueries({ queryKey: ["notifications"] }); };
  const openItem = async (n) => {
    if (!n.read) { await api.post(`/notifications/${n.id}/read`); qc.invalidateQueries({ queryKey: ["notifications"] }); }
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid="notification-bell" className="relative rounded-full p-2 text-slate-500 hover:bg-slate-100">
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span data-testid="notification-count" className="absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[10px] font-bold text-white" style={{ background: accent }}>
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
          <span className="font-bold text-[#263871]">Notifications</span>
          {unread > 0 && <button data-testid="mark-all-read" onClick={readAll} className="flex items-center gap-1 text-xs font-medium text-[#1D9BF0]"><Check className="h-3.5 w-3.5" />Tout lire</button>}
        </div>
        <div className="max-h-80 overflow-auto">
          {items.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-slate-400">Aucune notification</p>
          ) : items.map((n) => (
            <button key={n.id} data-testid={`notif-${n.id}`} onClick={() => openItem(n)} className={`flex w-full flex-col items-start border-b border-slate-50 px-4 py-3 text-left hover:bg-slate-50 ${!n.read ? "bg-[#E0F2FE]/40" : ""}`}>
              <span className="flex items-center gap-2 text-sm font-semibold text-[#263871]">{!n.read && <span className="h-2 w-2 rounded-full bg-[#1D9BF0]" />}{n.title}</span>
              <span className="mt-0.5 text-xs text-slate-500">{n.body}</span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
